'use client'

import { useState } from 'react'
import { getIdToken } from 'firebase/auth'
import { useTranslation } from 'react-i18next'
import { ClipLoader } from 'react-spinners'
import { FaEdit, FaSave, FaChevronDown, FaLock } from 'react-icons/fa'
import { MdPsychology, MdRecordVoiceOver } from 'react-icons/md'

const FALLBACK_AVATAR =
  'https://gravatar.com/avatar/f5862edde16dd6f7a1e30905b4096241?s=200&d=wavatar&r=x'

// Per-character settings for one group member. Groups derive each character's
// personality/voice straight from the user's own character doc (user_profiles),
// so editing here mutates that shared doc via the same 1:1 endpoints — it takes
// effect in the group (re-read each turn) and in your 1:1 chat with them.
const GroupMemberSettings = ({
  character,
  user,
  isPlusOrHigher,
  voices = [],
  onUpdate,
  onUpgrade,
}) => {
  const { t } = useTranslation()
  const id = character?.id

  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [voiceOpen, setVoiceOpen] = useState(false)

  const personality = character?.personality || ''
  const avatar = (character?.imageUrl || [])[0] || FALLBACK_AVATAR
  const currentVoice = voices.find((v) => v.code === character?.voice)

  const savePersonality = async () => {
    if (!user || saving) return
    const value = draft.trim()
    if (!value || value === personality) return
    setSaving(true)
    setError('')
    try {
      const idToken = await getIdToken(user)
      const res = await fetch(
        `${process.env.NEXT_PUBLIC_PROMPTCHAN_CLOUD}/api/edit_personality`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: idToken,
          },
          body: JSON.stringify({ id, new_personality: value }),
        },
      )
      const data = await res.json().catch(() => ({}))
      if (res.ok) {
        onUpdate?.(id, { personality: data.new_personality ?? value })
        setEditing(false)
      } else if (res.status === 300) {
        // Surface the rejection inline and keep the draft — a failed save used
        // to look like nothing happened at all.
        setError(
          data?.banned_word
            ? t('bannedWordDetectedNamed', {
                word: data.banned_word,
                defaultValue: `Banned word detected: ${data.banned_word}.`,
              })
            : t('bannedWordDetected', {
                defaultValue: 'Banned word detected.',
              }),
        )
      } else {
        setError(
          t('somethingWentWrong', {
            defaultValue: 'Something went wrong. Please try again.',
          }),
        )
      }
    } catch (e) {
      console.error('Failed to edit personality:', e)
      setError(
        t('somethingWentWrong', {
          defaultValue: 'Something went wrong. Please try again.',
        }),
      )
    } finally {
      setSaving(false)
    }
  }

  const setVoice = async (voiceCode) => {
    if (!user) return
    try {
      const idToken = await getIdToken(user)
      const res = await fetch(
        `${process.env.NEXT_PUBLIC_PROMPTCHAN_CLOUD}/api/set_voice`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: idToken,
          },
          body: JSON.stringify({ character_id: id, voice: voiceCode }),
        },
      )
      if (res.ok) onUpdate?.(id, { voice: voiceCode })
    } catch (e) {
      console.error('Failed to set voice:', e)
    }
  }

  return (
    <div className="rounded-xl border border-[var(--secondary-bg-color)] p-3 mb-3">
      {/* Member header */}
      <div className="flex items-center gap-2 mb-3">
        <img
          src={avatar}
          alt=""
          className="w-8 h-8 rounded-full object-cover object-top shrink-0"
        />
        <span className="text-white font-semibold text-sm truncate">
          {character?.name || 'Anon'}
        </span>
      </div>

      {/* Personality */}
      <div className="flex items-center justify-between mb-2">
        <span className="text-white text-xs font-semibold flex items-center">
          <MdPsychology className="mr-1 shrink-0" size={12} />
          {t('personality', { defaultValue: 'Personality' })}
        </span>
        {!editing ? (
          <button
            onClick={() => {
              setDraft(personality)
              setError('')
              setEditing(true)
            }}
            className="px-2.5 py-1 rounded-lg bg-[var(--foreground-bg-color)] text-white text-xs font-semibold flex items-center gap-1.5"
          >
            <FaEdit size={11} />
            {t('edit')}
          </button>
        ) : (
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => {
                setError('')
                setEditing(false)
              }}
              className="px-2.5 py-1 rounded-lg bg-[var(--foreground-bg-color)] text-white text-xs font-semibold"
            >
              {t('cancel')}
            </button>
            <button
              onClick={savePersonality}
              disabled={saving || !draft.trim() || draft.trim() === personality}
              className="px-2.5 py-1 rounded-lg bg-[var(--main-bg-color)] text-white text-xs font-semibold flex items-center gap-1.5 disabled:opacity-50"
            >
              {saving ? (
                <ClipLoader size={11} color="#fff" />
              ) : (
                <FaSave size={11} />
              )}
              {t('save', { defaultValue: 'Save' })}
            </button>
          </div>
        )}
      </div>

      {!editing ? (
        <p className="italic text-xs font-light bg-[var(--foreground-bg-color)] p-2.5 rounded-xl text-white leading-relaxed break-words">
          {personality.trim()
            ? `"${personality}"`
            : t('noPersonalitySet', {
                defaultValue: 'No personality set yet.',
              })}
        </p>
      ) : (
        <>
          <textarea
            value={draft}
            maxLength={300}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={t('enterYourPersonality', {
              defaultValue: 'Enter personality',
            })}
            className="rounded-xl px-3 py-2 w-full text-white bg-[var(--foreground-bg-color)] focus:outline-none min-h-[72px] text-sm resize-none"
          />
          {error && <p className="mt-1.5 text-xs text-red-400">{error}</p>}
        </>
      )}

      {/* Voice */}
      <div className="mt-3">
        <span className="text-white text-xs font-semibold flex items-center mb-1.5">
          <MdRecordVoiceOver className="mr-1 shrink-0" size={12} />
          {t('voice')}
        </span>
        <div className="relative">
          <button
            onClick={() => setVoiceOpen((o) => !o)}
            className="w-full min-h-[36px] bg-[var(--foreground-bg-color)] px-3 py-2 rounded-full text-white flex font-semibold items-center justify-between gap-2 hover:opacity-90 transition-opacity text-xs"
          >
            <span className="truncate">
              {currentVoice?.title || t('selectVoice')}
            </span>
            <FaChevronDown className="text-xs shrink-0" />
          </button>
          {voiceOpen && (
            <div className="absolute mt-1.5 right-0 left-0 bg-[#1E1D1E] rounded-2xl border border-white/10 shadow-lg z-50 max-h-56 overflow-y-auto grid grid-cols-2 p-1">
              {voices.map((v) => (
                <div
                  key={v.code}
                  onClick={() => {
                    if (v.plan === 'Plus' && !isPlusOrHigher) {
                      onUpgrade?.()
                    } else {
                      setVoice(v.code)
                    }
                    setVoiceOpen(false)
                  }}
                  className={`px-3 py-2 cursor-pointer hover:bg-[#333] rounded-xl flex items-center justify-center text-center ${
                    v.code === character?.voice
                      ? 'font-medium text-white bg-white/5'
                      : 'text-gray-300'
                  }`}
                >
                  <p className="text-xs">{v.title}</p>
                  {v.plan === 'Plus' && !isPlusOrHigher && (
                    <FaLock className="ml-1 shrink-0" size={9} />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default GroupMemberSettings
